import { useRef, useState, type FormEvent } from 'react';
import { api, type Usuario } from '../lib/api.ts';
import { CampoDoPasso } from '../componentes/PassoAPasso.tsx';

/*
 * ⭐ **O campo novo — 19/09/2026 (PRD §47.3).** Os campos das telas de entrada
 * passam a ser os do desenho de Imóveis (`CampoDoPasso`), como todo formulário
 * do Hub; a moldura da página continua a da entrada. ⛔ Muda o desenho, não o
 * que o botão faz: os mesmos `id` e `autoComplete` — é por eles que o
 * gerenciador de senhas acha o campo —, as mesmas rotas e os mesmos corpos.
 */

type Modo = 'entrar' | 'esqueci';

export function Login({ aoEntrar }: { aoEntrar: (usuario: Usuario) => void }) {
  const [modo, setModo] = useState<Modo>('entrar');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [recado, setRecado] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const emVoo = useRef(false);

  function trocarModo(novo: Modo) {
    setModo(novo);
    setErro(null);
    setRecado(null);
    setSenha('');
  }

  async function entrar(evento: FormEvent) {
    evento.preventDefault();
    if (emVoo.current) return;
    emVoo.current = true;
    setEnviando(true);
    setErro(null);
    try {
      aoEntrar(await api.entrar(email, senha));
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui entrar.');
      setSenha('');
    } finally {
      emVoo.current = false;
      setEnviando(false);
    }
  }

  async function pedirRedefinicao(evento: FormEvent) {
    evento.preventDefault();
    if (emVoo.current) return;
    emVoo.current = true;
    setEnviando(true);
    setErro(null);
    try {
      // A resposta é a mesma exista o e-mail ou não — de propósito. Esta tela
      // não pode virar um jeito de descobrir quem tem conta na KAIRÓS.
      const r = await api.esqueciSenha(email);
      setRecado(r.mensagem);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui enviar o pedido.');
    } finally {
      emVoo.current = false;
      setEnviando(false);
    }
  }

  return (
    <div className="login-fundo">
      {/* O logo vem do arquivo da marca e fica SOBRE o fundo Púrpura Noturno,
          não dentro do cartão branco: o manual (§3) manda usar a versão branca
          sobre fundo escuro, e é a única versão que temos. */}
      <div className="login-marca">
        <img src="/marca/kairos-branco.png" alt="KAIRÓS Asset Intelligence" />
        <span className="marca-hub">Hub</span>
      </div>

      <div className="login-caixa">
        {modo === 'entrar' ? (
          <form onSubmit={entrar} noValidate>
            {erro && <p className="erro">{erro}</p>}

            <div className="login-campos">
              <CampoDoPasso rotulo="E-mail" col={6}>
                <input
                  id="email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                />
              </CampoDoPasso>
              <CampoDoPasso rotulo="Senha" col={6}>
                <input
                  id="senha"
                  type="password"
                  autoComplete="current-password"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  required
                />
              </CampoDoPasso>
            </div>

            <button
              type="submit"
              className="botao-primario login-botao"
              disabled={enviando || !email || !senha}
            >
              {enviando ? 'Entrando…' : 'Entrar'}
            </button>

            <button
              type="button"
              className="botao-texto login-link"
              onClick={() => trocarModo('esqueci')}
            >
              Esqueci minha senha
            </button>
          </form>
        ) : (
          <form onSubmit={pedirRedefinicao} noValidate>
            {erro && <p className="erro">{erro}</p>}

            {recado ? (
              <p className="aviso-bom">{recado}</p>
            ) : (
              <>
                <p className="ajuda ajuda-solta">
                  Informe o seu e-mail. Se ele estiver cadastrado, você recebe um link
                  para escolher uma senha nova.
                </p>

                <div className="login-campos">
                  <CampoDoPasso rotulo="E-mail" col={6}>
                    <input
                      id="email-esqueci"
                      type="email"
                      autoComplete="username"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoFocus
                    />
                  </CampoDoPasso>
                </div>

                <button
                  type="submit"
                  className="botao-primario login-botao"
                  disabled={enviando || !email}
                >
                  {enviando ? 'Enviando…' : 'Enviar o link'}
                </button>
              </>
            )}

            <button
              type="button"
              className="botao-texto login-link"
              onClick={() => trocarModo('entrar')}
            >
              Voltar para a entrada
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
